import { identityOf, type ResolvedIdentity } from '@/shared/permissions/identity';
import { isRbacAllowed } from '@/shared/permissions/rbac';

export interface AIAuthorityUser {
  role?: string | null;
  displayName?: string | null;
  display_name?: string | null;
  department?: string | null;
  jobRole?: string | null;
  /** Client-presented authority claims are deliberately ignored. */
  tier?: unknown;
  authorityTier?: unknown;
  isSuperAdmin?: unknown;
}

export type AIAttributionKind = 'generation' | 'workflow-run' | 'activity-entry';

export interface AIFeatureDescriptor {
  id: string;
  label: string;
  path?: string;
  executiveOnly?: boolean;
}

export interface AIExecutiveUiGate {
  navigation: boolean;
  search: boolean;
  deepLinks: boolean;
  featureDiscovery: boolean;
}

export interface AIAuthorityPresentation extends ResolvedIdentity {
  executiveFeatures: Readonly<AIExecutiveUiGate>;
}

export interface AIAttribution {
  kind: AIAttributionKind;
  tier: ResolvedIdentity['tier'];
  attribution: string;
}

const INTERNAL_EXECUTIVE_LABEL = /super\s*admin/i;
const EXECUTIVE_POLICY = { authorityTiers: ['SUPER_ADMIN'] as const };

export function isAIFeaturePermitted(
  feature: AIFeatureDescriptor,
  user: AIAuthorityUser | null | undefined,
): boolean {
  return !feature.executiveOnly || isRbacAllowed(user, EXECUTIVE_POLICY);
}

function safeDisplayName(user: AIAuthorityUser | null | undefined): string | undefined {
  const candidate = user?.displayName?.trim() || user?.display_name?.trim();
  return candidate && !INTERNAL_EXECUTIVE_LABEL.test(candidate) ? candidate : undefined;
}


/**
 * Feature-local mirror of canonical authority. It controls presentation only;
 * API authorization remains exclusively server-side.
 */
export function aiAuthorityOf(
  user: AIAuthorityUser | null | undefined,
): AIAuthorityPresentation {
  const identity = identityOf({ role: user?.role, displayName: safeDisplayName(user) });
  const discoverable = isRbacAllowed(user, EXECUTIVE_POLICY);

  return {
    ...identity,
    executiveFeatures: Object.freeze({
      navigation: discoverable,
      search: discoverable,
      deepLinks: discoverable,
      featureDiscovery: discoverable,
    }),
  };
}

export function aiAttributionOf(
  user: AIAuthorityUser | null | undefined,
  kind: AIAttributionKind,
): AIAttribution {
  const identity = aiAuthorityOf(user);
  return { kind, tier: identity.tier, attribution: identity.attribution };
}

export function visibleAIFeatures<T extends AIFeatureDescriptor>(
  features: readonly T[],
  user: AIAuthorityUser | null | undefined,
): T[] {
  const gate = aiAuthorityOf(user).executiveFeatures.navigation;
  return features.filter((feature) => !feature.executiveOnly || gate);
}

export function searchAIFeatures<T extends AIFeatureDescriptor>(
  features: readonly T[],
  user: AIAuthorityUser | null | undefined,
  query: string,
): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [];
  const gate = aiAuthorityOf(user).executiveFeatures.search;
  return features.filter(
    (feature) =>
      (!feature.executiveOnly || gate) &&
      feature.label.toLowerCase().includes(normalizedQuery),
  );
}

export function resolveAIFeatureDeepLink<T extends AIFeatureDescriptor>(
  features: readonly T[],
  user: AIAuthorityUser | null | undefined,
  requestedIdOrPath: string | null | undefined,
): T | null {
  const requested = requestedIdOrPath?.trim().split(/[?#]/, 1)[0].replace(/\/$/, '');
  if (!requested) return null;

  const feature = features.find(
    (candidate) => candidate.id === requested || candidate.path?.replace(/\/$/, '') === requested,
  );
  if (!feature) return null;
  if (feature.executiveOnly && !aiAuthorityOf(user).executiveFeatures.deepLinks) return null;
  return feature;
}